/**
 * The one home of every failure and outcome sentence (rd round 12, lane A5a,
 * item A5-2; rc-5 §1d and §3B, rc-2 RC-B).
 *
 * WHY THIS EXISTS. About thirty functions in the portal each turn an error into
 * words, five `failureDetail` / `serverMessage` copies exist, and
 * `AdminApiError.message` — "Admin API request failed (500)" — reaches faces.
 * The owner was shown "That did not save. Refresh the page and try again." on a
 * 403 they can never clear (owner decision 1 below). One home for the sentences
 * means a page cannot invent a sixth wording, and the guards in this lane fail a
 * literal that reappears elsewhere.
 *
 * THE RULES, in one place:
 *   - OWNER DECISION 1 (orchestrator's recommendation, accepted by the owner):
 *     "When the owner is refused (403), show \"You do not have access to do
 *     that.\" Do not say \"Refresh the page\"."
 *   - The account manager keeps round 10's sentence for a 403, "Ask the owner."
 *     (rd-z-fixes-background.md §3); the owner is never told to ask the owner.
 *   - A write that did not save says so ("That did not save. Try again.") only
 *     when the server refused it (an `AuthoredRefusal` or a 4xx). A 5xx, a
 *     proxy timeout (408/425/499), a lost response, or an error thrown after a
 *     2xx (a body the API could not parse) may have landed, and it reads
 *     exactly as `outcomeSentence` words the same event: "We could not confirm
 *     it saved…".
 *   - A read that failed says it could not load. It is never worded as a write:
 *     no "did not save", no "Nothing changed.", no "already done". A read of a
 *     row the server no longer has says it is no longer there.
 *   - "That is already done." is the decided sentence for a 404 that can never
 *     pass (rd round 11 §4). A 409 nobody verified does not claim it: it says
 *     the row changed while the person was working.
 *   - `detail` carries the server's scrubbed words and is only ever shown inside
 *     a "Show details" disclosure. A plain `Error`'s message is not a sentence a
 *     person reads, so it is never the sentence; only a caller that asks for
 *     `localDetail` (the shell's config-error banner) gets it as `detail`, and
 *     never for a request that did not reach GV Books. Only an
 *     `AuthoredRefusal` (a sentence a page wrote for a person) is used as the
 *     sentence itself.
 *
 * This file repeats none of the *decided* sentences that belong to
 * `ui/sentences.ts` (lane A5b); it owns the failure and outcome table only.
 */
import { amVisibleDisplayText } from "../../../ingest-worker/src/am_visible_text.js";
import { AdminApiError } from "../api.js";
import { ConsoleDataError } from "../console/data-source.js";
import type { StaffRole } from "../types.js";
import type { WriteOutcome } from "./write.js";

/** rd Z1 / rd-polish-rules §1: one sentence for a write that did not save. */
const WRITE_FAILED = "That did not save. Try again.";
/** The same sentence, naming the one cause the person can act on. */
const WRITE_FAILED_CONNECTION = "That did not save. Check your connection and try again.";
/** A read that failed is not a write that failed. */
const READ_FAILED = "We could not load that just now. Try again.";
const READ_FAILED_CONNECTION = "We could not reach GV Books. Check your connection and try again.";
/** rd-polish-rules §1: only an account manager is told to ask the owner. */
const ASK_THE_OWNER = "Ask the owner.";
/** OWNER DECISION 1 (2026-10-31). */
const NOT_ALLOWED = "You do not have access to do that.";
const SIGN_IN_EXPIRED = "Your sign-in has expired. Refresh the page to sign in again.";
const ALREADY_DONE = "That is already done. Refresh to see it.";
/**
 * The decided sentence for a card another tab already answered (rd-z §3,
 * rd-r11 §4). It belongs to the card context: a row-shaped 409 on a card is the
 * "someone answered it" case, and the generic conflict wording would replace a
 * sentence the owner already reads on the queue and home pages.
 */
const ALREADY_ANSWERED_CARD = "Someone already answered this card. Refresh to see it.";
/**
 * A conflict nobody verified. "That is already done." is the decided 409/404
 * sentence, and it is only true when the server said the thing exists; a 409
 * can also mean someone else changed the row first, so the unverified case
 * says what is known and nothing more.
 */
const CONFLICT_CHANGED = "This changed while you were working. Refresh to see the latest, then try again.";
/** A read of a row the server no longer has: gone, not "already done". */
const READ_GONE = "That is no longer there. Refresh to see the latest.";
const TOO_MANY_TRIES = "Too many tries just now. Wait a moment and try again.";
const OUTCOME_SAVED = "Saved.";
const OUTCOME_NOTHING_CHANGED = "Nothing changed.";
const OUTCOME_UNCONFIRMED = "We could not confirm it saved. Refresh to see the latest, then try again.";
/**
 * A retry `runWrite` refused because the first attempt may have landed. It is
 * not the loop-inviting "refresh, then try again": the page must check (a
 * read-back) or adopt what exists, then call `forgetPossiblySent`.
 */
const OUTCOME_BLOCKED = "A request like this may already have gone through. Check before trying again.";
/** The fallback detail when nothing scrubbed survives. */
const NO_DETAIL = "Request failed";

export interface FailureContext {
  role: StaffRole;
  /**
   * `card` is a row on the queue/home card list: its decided 409 sentence is
   * "Someone already answered this card…", not the generic conflict wording.
   */
  context: "write" | "read" | "card";
  /**
   * Carry a plain `Error`'s own message as `detail` (never as the sentence).
   * Only the shell's config-error banner asks for this: it is the one place
   * whose failure is a setting the console itself threw, and its developer
   * message belongs behind "Show details" (round 12, B2a round 2). Every other
   * caller keeps the server-words-only rule.
   */
  localDetail?: boolean;
}

export interface FailureCopy {
  sentence: string;
  /** The server's words, scrubbed; show only inside a disclosure. */
  detail: string | null;
}

/**
 * A refusal a page wrote for a person (a validation sentence it can defend).
 * The only case where `Error.message` becomes a sentence; everything else gets a
 * decided sentence and, at most, scrubbed detail behind a disclosure.
 */
export class AuthoredRefusal extends Error {
  constructor(message: string) {
    super(message);
    this.name = "AuthoredRefusal";
  }
}

/**
 * The one error-to-words function. A 403 is the only role-dependent sentence;
 * everything else reads the same for both roles.
 */
export function failureCopy(error: unknown, ctx: FailureContext): FailureCopy {
  const status = statusOf(error);
  const detail = detailOf(error, ctx.localDetail === true);

  if (status === 403) {
    return {
      sentence: ctx.role === "account_manager" ? ASK_THE_OWNER : NOT_ALLOWED,
      detail,
    };
  }
  if (error instanceof AuthoredRefusal) {
    return { sentence: error.message, detail: null };
  }
  if (status === 401) return { sentence: SIGN_IN_EXPIRED, detail };
  if (status === 429) return { sentence: TOO_MANY_TRIES, detail };

  // A read is never worded as a write: it did not change anything, so it can
  // never read "did not save", "Nothing changed." or "already done".
  if (ctx.context === "read") {
    if (status === 404) return { sentence: READ_GONE, detail };
    if (status === 409) return { sentence: CONFLICT_CHANGED, detail };
    if (isUnreachable(error)) return { sentence: READ_FAILED_CONNECTION, detail: null };
    return { sentence: READ_FAILED, detail };
  }

  // A write. Only a refusal the server actually made can say nothing was
  // saved. A 5xx, a proxy timeout or a lost response may have landed, and it
  // must read exactly as `outcomeSentence` words the same event. So must an
  // error the page threw itself: `api.ts` parses the body of a 2xx, so a
  // `SyntaxError` after the server acted means the write DID land — the old
  // "never reached a server answer" assumption is false (round-2 N3).
  if (status === 404) return { sentence: ALREADY_DONE, detail };
  if (status === 409) {
    return {
      sentence: ctx.context === "card" ? ALREADY_ANSWERED_CARD : CONFLICT_CHANGED,
      detail,
    };
  }
  if (status !== null && isMayHaveLanded(status)) {
    return { sentence: OUTCOME_UNCONFIRMED, detail: detail ?? null };
  }
  if (isUnreachable(error)) return { sentence: OUTCOME_UNCONFIRMED, detail: null };
  if (status !== null && status >= 400 && status < 500) {
    return { sentence: WRITE_FAILED, detail };
  }
  return { sentence: OUTCOME_UNCONFIRMED, detail };
}

/** Statuses where the request may have reached the server (rc-5: never "did not save"). */
function isMayHaveLanded(status: number): boolean {
  return status >= 500 || status === 408 || status === 425 || status === 499;
}

/**
 * The outcome table: the caller's only way to turn a `WriteOutcome` into words.
 * `unchanged` lets a page whose action is "nothing should have changed" keep its
 * own line, and it is used only for a refusal that sent nothing.
 */
export function outcomeSentence(
  outcome: WriteOutcome,
  role: StaffRole,
  unchanged?: string,
): string {
  switch (outcome.kind) {
    case "landed":
      return OUTCOME_SAVED;
    case "unknown":
      return outcome.blocked === true ? OUTCOME_BLOCKED : OUTCOME_UNCONFIRMED;
    case "conflict":
      // Unverified by definition: `runWrite` turns a verified 409 into landed.
      return CONFLICT_CHANGED;
    case "refused": {
      if (outcome.status === 0) return unchanged ?? OUTCOME_NOTHING_CHANGED;
      if (outcome.status === 403) return role === "account_manager" ? ASK_THE_OWNER : NOT_ALLOWED;
      if (outcome.status === 401) return SIGN_IN_EXPIRED;
      if (outcome.status === 404) return ALREADY_DONE;
      if (outcome.status === 429) return TOO_MANY_TRIES;
      return unchanged ?? OUTCOME_NOTHING_CHANGED;
    }
  }
}

/**
 * The pre-round-12 wording of the shared announcer (`runAnnouncedAction`),
 * kept while pages are still unmigrated.
 *
 * WHY IT IS NOT `failureCopy`. Every one of the twenty call sites predates
 * decision 1 and pins today's sentence in its own test
 * (`rd-r11-8b-books-health.test.ts` asserts an owner's 403 reads "That did not
 * save. Try again."). Switching the shared announcer to decision 1's sentence
 * in wave A would change ~20 pages' words at once — that is wave B's job
 * ("wave B moves the pages"), and the test that pins it is not this lane's to
 * edit. So the owner's 403 sentence lives in `failureCopy` (built here, in its
 * one home) and is adopted page by page; this function keeps the old behaviour
 * and no longer holds a literal of its own.
 */
export function legacyAnnouncedFailureSentence(
  error: unknown,
  role: StaffRole | null,
): string {
  if (statusOf(error) === 403 && role === "account_manager") return ASK_THE_OWNER;
  if (error instanceof AdminApiError || error instanceof ConsoleDataError) return WRITE_FAILED;
  return isUnreachable(error) ? WRITE_FAILED_CONNECTION : WRITE_FAILED;
}

/**
 * A caught error's words for a "Show details" disclosure, scrubbed. Kept as the
 * module's public detail accessor for the pages that already call it; new code
 * reads `failureCopy(...).detail` instead.
 */
export function actionErrorMessage(error: unknown): string {
  if (error instanceof AdminApiError) {
    const fromBody = adminErrorBodyMessage(error.body);
    if (fromBody !== null) return amVisibleDisplayText(fromBody);
    if (error.status === 403) return "denied (403)";
    return amVisibleDisplayText(error.message);
  }
  if (error instanceof ConsoleDataError) return amVisibleDisplayText(error.message);
  if (error instanceof Error) return amVisibleDisplayText(error.message);
  return NO_DETAIL;
}

/** True when the request never reached GV Books (a rejected fetch). */
export function isUnreachable(error: unknown): boolean {
  return (
    error instanceof Error && /failed to fetch|networkerror|network error|load failed/i.test(error.message)
  );
}

function statusOf(error: unknown): number | null {
  if (error instanceof AdminApiError || error instanceof ConsoleDataError) return error.status;
  return null;
}

function detailOf(error: unknown, includeLocal: boolean): string | null {
  if (error instanceof AdminApiError) {
    const fromBody = adminErrorBodyMessage(error.body);
    if (fromBody !== null) return amVisibleDisplayText(fromBody);
    return amVisibleDisplayText(error.message);
  }
  if (error instanceof ConsoleDataError) return amVisibleDisplayText(error.message);
  // A plain `Error` the console itself threw (a missing setting, a broken
  // invariant) is developer detail, never a sentence: the sentence still comes
  // from the table above. Only a caller that asked for local detail gets it
  // (`localDetail`), and a request that never reached the server has none.
  if (includeLocal && error instanceof Error) {
    if (isUnreachable(error)) return null;
    const message = amVisibleDisplayText(error.message);
    return message.length > 0 ? message : null;
  }
  return null;
}

function adminErrorBodyMessage(body: unknown): string | null {
  if (body === null || typeof body !== "object") return null;
  const err = (body as { error?: unknown }).error;
  if (err === null || typeof err !== "object") return null;
  const message = (err as { message?: unknown }).message;
  if (typeof message !== "string") return null;
  const trimmed = message.trim();
  return trimmed.length > 0 ? trimmed : null;
}
